// Shared building blocks of the two filter panels (FacetsPanel for products, DocumentFacetsPanel
// for documents), so both look and behave the same inside the shared filter drawer:
//
// - FacetGroup: one facet as a collapsible section - bold heading, chevron, the whole header row
//   clickable. Its collapsed state lives in a Jotai atom (see state.collapsedFacetGroupsAtom),
//   NOT in component state: the drawer unmounts its content on close, and a group the user
//   folded away deliberately should still be folded the next time they open the drawer.
// - FacetValueList: a value list capped at VALUE_LIST_LIMIT rows with a "N weitere anzeigen"
//   toggle below. With 30+ languages (see the Sprache facet in the document filter) an uncapped
//   list pushed every other facet below the fold and made the drawer endlessly long. Selected
//   values are always shown, even beyond the cap, so an active filter can never hide behind the
//   toggle where the user wouldn't find it to deselect it. Past VALUE_LIST_SEARCH_THRESHOLD
//   values (the document Edition facet has ~190 months) a search field sits above the list:
//   typing narrows it to matching labels - all of them, the cap steps aside while a query is
//   active - so a value is found instead of paged through ("180 weitere anzeigen").
// - CountedLabel + FACET_ROW_STYLES: the "label ... count" row of a value with the count badge
//   RIGHT-aligned in one straight column. Mantine's Checkbox/Radio label wrapper is `inline-flex`
//   and only as wide as its text, so a badge glued to the label ended at a different x on every
//   row (the "Flatterrand" from the review). FACET_ROW_STYLES stretches the wrapper to the full
//   row width - as an inline `styles` prop rather than a stylesheet rule, because @uag/doka-design's
//   Tailwind layer has a history of silently out-cascading Mantine class rules here (see App.css).

import { Collapse, Group, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { useAtom } from 'jotai';
import { useState, type ReactNode } from 'react';

import { normalize } from '../engine';
import { useCatalogLabels } from '../labels';
import { collapsedFacetGroupsAtom } from '../state';
import { selectRightSection, selectRightSectionPointerEvents } from './selectAffordances';

/** Rows shown before a value list folds the rest behind "N weitere anzeigen". */
export const VALUE_LIST_LIMIT = 10;

/** From this many values on, a value list gets a search field above it. */
export const VALUE_LIST_SEARCH_THRESHOLD = 20;

/** Class for the small text actions in the drawer ("Zurücksetzen", "N weitere anzeigen"): a
 * dark-blue text link, see App.css. Under @uag/doka-design `variant="subtle"` rendered as a solid
 * dark-blue block, and the outlined box that replaced it still shouted louder than the facets. */
export const TEXT_ACTION_CLASS = 'pih-text-action';

/** `styles` for every Checkbox/Radio inside a facet value list - see the file comment. */
export const FACET_ROW_STYLES = {
    labelWrapper: { flex: '1 1 auto', minWidth: 0 },
    label: { display: 'block' },
} as const;

/** The (possibly long) label shrinks and wraps, the count doesn't, and it sits at the right edge
 * of the row - the same edge for every value, so the counts form one column. A plain grey number
 * rather than a badge: a coloured pill on every row made the list look busy (reported: "Die Filter
 * sehen noch nicht richtig schön aus"). */
export const CountedLabel = ({ text, count }: { text: string; count: number }) => (
    <Group gap={8} justify="space-between" wrap="nowrap">
        <span style={{ minWidth: 0, color: 'var(--pih-text)' }}>{text}</span>
        <span
            style={{ flex: 'none', color: 'var(--pih-muted)', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}
        >
            {count}
        </span>
    </Group>
);

interface FacetGroupProps {
    /** Stable identity for the remembered collapsed state - the facet key, or a fixed id for the
     * document facets. Must be unique across BOTH panels (they share the atom). */
    groupKey: string;
    title: string;
    /** What this group currently filters by, as text ("0,1 – 2 kg", "AllCountries, Doka
     * Österreich") - shown under the title while the group is folded, so a folded group still
     * tells WHAT it filters. Replaces a numeric badge next to the title, which read as an
     * unexplained icon (reported: "Bedeutet '1' im blauen Badge 'aktiv'?" - for a von/bis field it
     * was always 1). An open group needs nothing: its own fields and ticks show the selection. */
    summary?: string;
    children: ReactNode;
}

export const FacetGroup = ({ groupKey, title, summary, children }: FacetGroupProps) => {
    const { L } = useCatalogLabels();
    const [collapsed, setCollapsed] = useAtom(collapsedFacetGroupsAtom);
    const opened = !collapsed.includes(groupKey);
    const toggle = () =>
        setCollapsed((prev) =>
            prev.includes(groupKey) ? prev.filter((key) => key !== groupKey) : [...prev, groupKey],
        );

    return (
        <div>
            <UnstyledButton
                aria-expanded={opened}
                aria-label={`${title}: ${opened ? L.facetCollapse : L.facetExpand}`}
                className="pih-facet-toggle flex w-full items-center justify-between gap-2"
                mb={opened ? 10 : 0}
                py={2}
                onClick={toggle}
            >
                <span className="flex min-w-0 flex-col items-start">
                    <Text
                        className="pih-facet-title"
                        component="span"
                        fw={600}
                        size="sm"
                        style={{ color: 'var(--pih-text)' }}
                    >
                        {title}
                    </Text>
                    {!opened && summary && (
                        <Text
                            component="span"
                            size="xs"
                            style={{ color: 'var(--pih-primary)', maxWidth: '100%', textAlign: 'left' }}
                            truncate
                        >
                            {summary}
                        </Text>
                    )}
                </span>
                <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 20, flex: 'none', color: 'var(--pih-muted)' }}
                    aria-hidden
                >
                    {opened ? 'expand_less' : 'expand_more'}
                </span>
            </UnstyledButton>
            <Collapse in={opened}>{children}</Collapse>
        </div>
    );
};

interface FacetValueListProps<T extends { value: string }> {
    entries: T[];
    /** Currently selected values - always rendered, even past the cap (see file comment). */
    selected: string[];
    /** Display text of a value - what the search field above a long list matches against. */
    labelOf: (entry: T) => string;
    renderEntry: (entry: T) => ReactNode;
}

export const FacetValueList = <T extends { value: string }>({
    entries,
    selected,
    labelOf,
    renderEntry,
}: FacetValueListProps<T>) => {
    const { L } = useCatalogLabels();
    const [expanded, setExpanded] = useState(false);
    const [query, setQuery] = useState('');

    const searchable = entries.length > VALUE_LIST_SEARCH_THRESHOLD;
    // engine.normalize, like every other search in the catalog: "osterreich" finds "Österreich".
    const needle = searchable ? normalize(query) : '';
    // A running search shows EVERY match (no cap - the query is the narrowing) plus the selected
    // values, so nothing active ever disappears while typing.
    const matching = needle
        ? entries.filter((entry) => selected.includes(entry.value) || normalize(labelOf(entry)).includes(needle))
        : entries;
    const capped = !needle && matching.length > VALUE_LIST_LIMIT;
    const visible =
        capped && !expanded
            ? matching.filter((entry, index) => index < VALUE_LIST_LIMIT || selected.includes(entry.value))
            : matching;
    const hidden = matching.length - visible.length;

    return (
        <Stack gap={8}>
            {searchable && (
                <TextInput
                    aria-label={L.facetSearchPlaceholder}
                    placeholder={L.facetSearchPlaceholder}
                    rightSection={selectRightSection(query.length > 0, () => setQuery(''))}
                    rightSectionPointerEvents={selectRightSectionPointerEvents(query.length > 0)}
                    size="xs"
                    value={query}
                    onChange={(event) => setQuery(event.currentTarget.value)}
                />
            )}
            {visible.map(renderEntry)}
            {needle && matching.length === 0 && (
                <Text c="dimmed" size="sm">
                    {L.noFilterMatch}
                </Text>
            )}
            {capped && (
                <UnstyledButton
                    className={`${TEXT_ACTION_CLASS} self-start`}
                    onClick={() => setExpanded((prev) => !prev)}
                >
                    {expanded ? L.facetShowLess : L.facetShowMore.replace('{count}', String(hidden))}
                </UnstyledButton>
            )}
        </Stack>
    );
};
